import React, { useState, useRef, useEffect, useCallback } from 'react';
import "./Getupdates.css";
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { getApiBaseUrl } from '../../apiBaseUrlFinder';

const Getupdates = () => {
  const [selectedManager, setSelectedManager] = useState(null);
  const [managerSearchTerm, setManagerSearchTerm] = useState("");
  const [managerSuggestions, setManagerSuggestions] = useState([]);
  const [showManagerSuggestions, setShowManagerSuggestions] = useState(false);
  const [email, setEmail] = useState("");
  const [managerLastKey, setManagerLastKey] = useState(null);
  const [viewMoreClicked, setViewMoreClicked] = useState(false);
  const [previousManagersCount, setPreviousManagersCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Ref to track managerLastKey without causing function recreation
  const managerLastKeyRef = useRef(null);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if all fields are filled to enable the subscribe button
  const isFormValid = selectedManager && email.trim() !== "" && isValidEmail(email);

  // Formik validation schema - not used currently as we use custom validation
  // const validationSchema = Yup.object().shape({
  //   companyName: Yup.string().required("Company name is required."),
  //   managerName: Yup.string().required("Manager name is required."),
  //   email: Yup.string().email("Invalid email format.").required("Personal email is required."),
  // });

  const initialValues = { managerName: "", email: "" };


  // Search for managers globally
  const searchManagers = useCallback(async (query, append = false) => {
    if (!query.trim()) {
      setManagerSuggestions([]);
      setManagerLastKey(null);
      return;
    }

    try {
      const params = new URLSearchParams({
        search_type: "managers",
        manager_full_name: query.trim(),
      });

      let url = `${getApiBaseUrl()}/search?${params.toString()}`;

      // Add last_key for pagination if appending
      if (append && managerLastKeyRef.current) {
        const lastKeyValue = typeof managerLastKeyRef.current === "string" ? managerLastKeyRef.current : JSON.stringify(managerLastKeyRef.current);
        url += `&last_key=${encodeURIComponent(lastKeyValue)}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data?.managers && Array.isArray(data.managers)) {
        const validManagers = data.managers.filter(Boolean);
        const managersWithLogo = validManagers.slice(0, 5).map((item) => ({
          name: item.manager_full_name,
          id: item.manager_id,
          company_name: item.company_name,
          company_id: item.company_id,
          position: item.position || "Unknown",
          location: item.location || "Unknown",
          company_logo_url: item.company_logo_url,
        }));

        if (append) {
          setManagerSuggestions((prev) => [...prev, ...managersWithLogo]);
        } else {
          setManagerSuggestions(managersWithLogo);
        }

        setManagerLastKey(data.last_key || null);
      } else {
        if (!append) {
          setManagerSuggestions([]);
          setManagerLastKey(null);
        }
      }
    } catch (error) {
      console.error("Error searching managers:", error);
      if (!append) {
        setManagerSuggestions([]);
        setManagerLastKey(null);
      }
    }
  }, []);


  // Keep managerLastKeyRef in sync with managerLastKey state
  useEffect(() => {
    managerLastKeyRef.current = managerLastKey;
  }, [managerLastKey]);

  // Debounce for manager search
  useEffect(() => {
    const handler = setTimeout(() => {
      searchManagers(managerSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [managerSearchTerm, searchManagers]);

  // Track suggestions count changes to detect when new records are loaded
  useEffect(() => {
    if (managerSuggestions.length > previousManagersCount && viewMoreClicked) {
      // Reset the clicked state after a delay
      setTimeout(() => {
        setViewMoreClicked(false);
      }, 100);
    }
    setPreviousManagersCount(managerSuggestions.length);
  }, [managerSuggestions.length, previousManagersCount, viewMoreClicked]);


  // Handle View More click
  const handleViewMoreClick = async () => {
    setViewMoreClicked(true);
    await searchManagers(managerSearchTerm, true);
  };

  // Close suggestions when clicking outside
  const managerInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        managerInputRef.current &&
        !managerInputRef.current.contains(event.target)
      ) {
        setShowManagerSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Handle manager selection
  const handleManagerSelect = (manager) => {
    setSelectedManager(manager);
    setManagerSearchTerm(manager.name);
    setShowManagerSuggestions(false);
    setManagerSuggestions([]);
  };

  // Check if field is active (focused or has value)
  const isFieldActive = (fieldName) => {
    return focusedField === fieldName || (fieldName === "managerName" && managerSearchTerm.trim() !== "") || (fieldName === "email" && email.trim() !== "");
  };

  // Handle subscription
  const handleSubmit = async (values) => {
    // Validate that manager is selected
    if (!selectedManager || !email.trim()) {
      toast.error("Please fill all required fields.");
      return;
    }

    // Validate email format
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        subscriber_email: email,
        manager_name: selectedManager.name,
        manager_id: selectedManager.id,
        company_name: selectedManager.company_name,
        company_id: selectedManager.company_id
      };

      const response = await fetch(`${getApiBaseUrl()}/manager_subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.message === "Subscribed!") {
        toast.success("Successfully subscribed to manager updates!");
        
        // Clear all fields
        setSelectedManager(null);
        setManagerSearchTerm("");
        setEmail("");
        setManagerSuggestions([]);
        setManagerLastKey(null);
        setPreviousManagersCount(0);
      } else {
        toast.error(data.message || "Failed to subscribe. Please try again.");
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='px-lg-2 px-md-2 px-2'>
      {/* Subtle UX cues for button */}
      <style>{`
        .subscribe_btn.coming-soon-btn{
          transition: transform .12s ease, box-shadow .2s ease, opacity .2s ease;
          will-change: transform;
        }
        .subscribe_btn.coming-soon-btn:hover:not(:disabled){
          transform: translateY(-1px) scale(1.01);
          box-shadow: 0 6px 16px rgba(0,0,0,.10);
        }
        .subscribe_btn.coming-soon-btn:active:not(:disabled){
          transform: translateY(0) scale(.98);
          box-shadow: 0 3px 10px rgba(0,0,0,.08);
        }
        .subscribe_btn.coming-soon-btn:disabled{
          transform: none !important;
          box-shadow: none !important;
        }
        @media (prefers-reduced-motion: reduce){
          .subscribe_btn.coming-soon-btn{ transition:none; }
        }
      `}</style>

      <div className='container'>
        <div className='row py-lg-5 py-md-4 py-3'>
          <div className='offset-lg-2 col-lg-8 col-md-12 col-12'>
            <div className='get_updates_box'>
              <h4 className='get_updates_h4 pt-4 text-center'>
                Get updates on manager profile
              </h4>
              <div className='row'>
                <Formik 
                  initialValues={initialValues} 
                  onSubmit={handleSubmit}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {({ handleSubmit }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="row pt-4 pb-4">
                        {/* Input Fields */}
                        <div className="col-lg-6 col-md-6 col-12">
                          {/* Manager Name - Searchable */}
                          <div className="form-group mb-3" ref={managerInputRef}>
                            <input
                              name="managerName"
                              type="text"
                              id="managerName"
                              className={`form-input dropdown-toggle ${
                                isFieldActive("managerName") ? "input-active" : ""
                              } label-medium`}
                              placeholder=" "
                              value={managerSearchTerm}
                              onChange={(e) => {
                                setManagerSearchTerm(e.target.value);
                                setShowManagerSuggestions(true);
                              }}
                              onFocus={() => {
                                setFocusedField("managerName");
                                setShowManagerSuggestions(true);
                              }}
                              onBlur={() => setFocusedField(null)}
                            />
                            <label
                              htmlFor="managerName"
                              className={`form-label-sub ${
                                isFieldActive("managerName") ? "label-active" : ""
                              }`}
                            >
                              Manager Name
                            </label>
                            {showManagerSuggestions && managerSuggestions.length > 0 && (
                              <ul className="sub-suggestion-list">
                                {managerSuggestions.map((manager) => (
                                  <li
                                    key={manager.id}
                                    onClick={() => handleManagerSelect(manager)}
                                    className="sub-suggestion-item"
                                  >
                                    {manager.company_logo_url && (
                                      <img
                                        src={manager.company_logo_url}
                                        alt="Company Logo"
                                        className="sub-suggestion-logo"
                                      />
                                    )}
                                    <div className="sub-suggestion-content">
                                      <div className="sub-suggestion-name">{manager.name}</div>
                                      <div className="sub-suggestion-details">
                                        {manager.company_name} • {manager.position} • {manager.location}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                                
                                {/* View More button */}
                                {managerLastKey && (
                                  <li
                                    className={`sub-view-more-btn ${viewMoreClicked ? 'loading' : ''}`}
                                    onClick={handleViewMoreClick}
                                  >
                                    {viewMoreClicked ? (
                                      <span className="sub-loading-spinner">
                                        <div className="sub-spinner" />
                                        Loading...
                                      </span>
                                    ) : (
                                      "View More"
                                    )}
                                  </li>
                                )}
                              </ul>
                            )}
                          </div>

                          {/* Email - Regular input */}
                          <div className="form-group mb-3">
                            <input
                              name="email"
                              type="email"
                              id="email"
                              className={`form-input ${
                                email.trim() !== "" && !isValidEmail(email) ? "input-error" : ""
                              } ${isFieldActive("email") ? "input-active" : ""} label-medium`}
                              placeholder=" "
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              onFocus={() => setFocusedField("email")}
                              onBlur={() => setFocusedField(null)}
                            />
                            <label
                              htmlFor="email"
                              className={`form-label-sub ${
                                isFieldActive("email") ? "label-active" : ""
                              }`}
                            >
                              Personal Email
                            </label>
                            {email.trim() !== "" && !isValidEmail(email) && (
                              <div className="error-message-sub">Please enter a valid email address</div>
                            )}
                          </div>
                        </div>

                        {/* Subscribe Button */}
                        <div className="col-lg-6 col-md-6 col-12 d-flex align-items-center justify-content-center">
                          <button
                            type="submit"
                            className={`subscribe_btn coming-soon-btn ${isFormValid && !isSubmitting ? 'sub-subscribe-btn-active' : 'sub-subscribe-btn-disabled'}`}
                            disabled={!isFormValid || isSubmitting}
                          >
                            {isSubmitting ? (
                              <span className="sub-loading-spinner">
                                <div className="sub-spinner" />
                                Subscribing...
                              </span>
                            ) : (
                              "Subscribe"
                            )}
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Getupdates;